import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { RelationshipManagerService } from '@core/services/relationship-manager.service';
import { Store } from '@ngxs/store';
import { ButtonDirective } from '@shared/directives/button.directive';
import { DropDownModel } from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Subscription, Observable, BehaviorSubject, take } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
@Component({
  selector: 'app-relationship-manager',
  standalone: true,
  imports: [
    FiltersComponent,
    TableSkeletonLoaderComponent,
    TableNavigatorComponent,
    RouterOutlet,
    CommonModule,
    ButtonDirective,
    PermissionDirective,
    TableRefreshComponent,
  ],
  templateUrl: './relationship-manager.component.html',
  styleUrl: './relationship-manager.component.scss',
})
export class RelationshipManagerComponent {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  showAction = false;
  actionList: DropDownModel[] = [];
  subs: Subscription[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  account: any;
  accountName: string | null = null;
  isExportDialogOpen: boolean = false;
  exportedKycUrl: string | null = null;
  isExportInProgress: boolean = false;
  accountType: string | null = null;
  $index: any;
  relationshipManagers: { key: string; value: string }[] = [];
  rmIds: Record<string, string> = {};
  currentRMId!: string

  isFetchingAccountList$!: Observable<boolean>;

  private accountListSubject = new BehaviorSubject<any>(null);
  private totalCountSubject = new BehaviorSubject<number>(0);
  private accountMetadataSubject = new BehaviorSubject<any>(null);
  private RMListSubject = new BehaviorSubject<any>(null);

  accountsList$ = this.accountListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  accountMetadata$: Observable<any> =
    this.accountMetadataSubject.asObservable();
  RMList$ = this.RMListSubject.asObservable();
  private hadFilterBefore = false;

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breadCrumbService = inject(BreadCrumbService);
  private change = inject(ChangeDetectorRef);
  private store = inject(Store);
  private relationshipMaanger = inject(RelationshipManagerService)
  private toast = inject(NotificationFacade)

  loading$ = this.store.select((state) => state.table.accounts?.loading);
  isFilterActive: boolean = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'relationship_manager',
      label: 'Relationship Manager',
      type: 'dropdown',
      dropdown: this.relationshipManagers,
    },
    {
      filterKey: 'account_type',
      label: 'Account Type',
      type: 'dropdown',
      dropdown: [
        { key: 'corporate', value: 'Corporate' },
        { key: 'individual', value: 'Individual' },
        { key: 'joint', value: 'Joint' },
      ],
    },
    {
      filterKey: 'kyc_status',
      label: 'KYC Status',
      type: 'dropdown',
      dropdown: [
        { key: 'completed', value: 'Completed' },
        { key: 'incomplete', value: 'Incomplete' },
      ],
    },
    {
      filterKey: 'rm_status',
      label: 'RM Status',
      type: 'dropdown',
      dropdown: [
        { key: 'yes', value: 'Assigned' },
        { key: 'no', value: 'Not Assigned' },
      ],
    },
  ];

  constructor() {
    this.accountsList$ = this.store.select(
      TableSelectors.getTableData('accountsRM')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('accountsRM')
    );
    this.isFetchingAccountList$ = this.store.select(
      TableSelectors.getTableLoading('accountsRM')
    );
    this.RMList$ = this.store.select(
      TableSelectors.getTableData('relationshipManagers')
    );
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.loadAccountsList();
    this.loadRMList();
    this.getAccounMetadata();
    this.filterAccountListEventListener();

    this.mapRMList();
  }

  loadAccountsList(page = 1) {
    const sub = this.accountsList$.pipe(take(1)).subscribe((accounts) => {
      const hasData = Array.isArray(accounts) && accounts.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'accountsRM',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  loadRMList(page = 1) {
    const sub = this.RMList$.pipe(take(1)).subscribe((manager) => {
      const hasData = Array.isArray(manager) && manager.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'relationshipManagers',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  mapRMList() {
  const sub = this.RMList$.subscribe((list) => {
    if (Array.isArray(list)) {
      this.relationshipManagers = list.map((rm: any) => {
        const first = rm.first_name ?? '';
        const last = rm.last_name ?? '';
        const value = first && last ? `${first.replace(" ", "")} ${last.replace(" ", "")}` : first || last || '';
        this.rmIds[value.replace(" ", "")] = rm.id
        return {
          key: value,
          value,
        };
      });
      this.relationshipManagers.unshift({ key: 'unassigned', value: 'Unassigned' });

      // Update only the relationship_manager dropdown
      const rmFilter = this.filterStructure.find(f => f.filterKey === 'relationship_manager');
      if (rmFilter) {
        rmFilter.dropdown = this.relationshipManagers;
      }

      this.change.detectChanges();
      console.log('rm', this.relationshipManagers);
    }
  });
  this.subs.push(sub);
}


  getAccounMetadata() {
    // if the account metadata is not in store, dispatch an action to get it
    const sub = this.accountMetadata$.subscribe((metadata) => {
      if (!metadata) {
        this.store.dispatch(new TableActions.GetAccountsRMMetadata());
      }
    });
    this.subs.push(sub);
  }

  getRMMetaData(RMId: string) {
    const sub = this.relationshipMaanger.getRMMetaData(RMId).subscribe({
      next: (response) => {
        this.accountMetadataSubject.next(response.data)
        this.change.detectChanges()
      },
      error: (err) => {
        this.toast.error(err)
      }
    })
    this.subs.push(sub)
  }

  get effectiveAccountMetadata$(): Observable<any> {
    return this.isFilterActive
      ? this.accountMetadataSubject.asObservable()
      : this.store.select(TableSelectors.getAccountsRMMetadata());
  }

  filterAccountListEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        if (params['relationship_manager'] === 'unassigned') {
          // "Unassigned" is a synthetic option, not a real RM - reuse the rm_status "not assigned" filter
          this.currentRMId = '';
          query += `relationship_manager=no&`;
        } else {
          // get rm id from rmids object
          this.currentRMId = this.rmIds[params['relationship_manager'].replace(" ", "")]
          this.getRMMetaData(this.currentRMId)
          this.change.detectChanges()
          query += `relationship_manager_id=${this.currentRMId}&`;
        }
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `account_type=${params['account_type']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `kyc_status=${params['kyc_status']}&`;
      }
      if (params[this.filterStructure[3].filterKey]) {
        query += `relationship_manager=${params['rm_status']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true; // filters are currently active
        this.store.dispatch(
          new TableActions.FilterAccountRMList({ filter: query })
        );
      } else {
        this.isFilterActive = false;

        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'accountsRM',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }
  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      if (i > 0) {
        return (
          p +
          ` & ${c.personal_details.first_name} ${c.personal_details.last_name?.toLocaleUpperCase()}`
        );
      }
      return `${c.personal_details.first_name} ${c.personal_details.last_name?.toLocaleUpperCase()}`;
    }, '');
  }

  viewAccountDetails(account: any) {
    this.router.navigateByUrl(
      `/admin/accounts/${account.account_type}/${account.id}`
    );
  }

  openKycDocument() {
    if (this.exportedKycUrl) {
      window.open(this.exportedKycUrl, '_blank');
    }
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }

  exportReport() {
    // Get the base path without query params
    const baseUrl = this.router.url.split('?')[0];

    this.router.navigateByUrl(
      `${baseUrl}/export-report${this.currentRMId ? `?rmid=${this.currentRMId}` : ''}`
    );
  }

  transferAccounts() {
    // Preserve the active filters (e.g. relationship_manager) instead of adding new query params
    this.router.navigate(['transfer'], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve',
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}

